/**
 * Renders payment links + product upsells on the confirmation page.
 * Stage-aware: filters product_upsells by current stage (pre/during/post/all).
 * Phase D feature - lightweight bridge until Phygital Stripe ships.
 */
import { Wallet, ShoppingBag, ExternalLink } from "lucide-react";

const PAYMENT_ICON_LABELS = {
  venmo: "Venmo",
  zelle: "Zelle",
  cashapp: "Cash App",
  applepay: "Apple Pay",
  paypal: "PayPal",
  link: "Pay",
};

const PAYMENT_ICON_COLORS = {
  venmo:    "bg-blue-50  text-blue-700  border-blue-200",
  zelle:    "bg-purple-50 text-purple-700 border-purple-200",
  cashapp:  "bg-green-50 text-green-700 border-green-200",
  applepay: "bg-gray-100 text-gray-900 border-gray-300",
  paypal:   "bg-blue-50  text-blue-800  border-blue-200",
  link:     "bg-purple-50 text-purple-700 border-purple-200",
};

export default function PaymentUpsellSection({ paymentLinks = [], productUpsells = [], paymentNote = "", currentStage = "post" }) {
  const stageProducts = productUpsells.filter(p => !p.stage || p.stage === "all" || p.stage === currentStage);
  const showPayments = paymentLinks.length > 0 && (currentStage === "post" || currentStage === "complete");
  const showProducts = stageProducts.length > 0;
  
  if (!showPayments && !showProducts) return null;
  
  return (
    <div className="space-y-3 mt-4" data-testid="payment-upsell-section">
      {showPayments && (
        <div className="rounded-2xl border border-gray-200 bg-white p-4" data-testid="payment-links-card">
          <div className="flex items-center gap-2 mb-3">
            <Wallet className="w-4 h-4 text-purple-600" />
            <p className="text-sm font-semibold text-gray-900">How to pay</p>
          </div>
          {paymentNote && <p className="text-xs text-gray-600 mb-3">{paymentNote}</p>}
          <div className="flex flex-wrap gap-2">
            {paymentLinks.map((pl, i) => {
              const cls = PAYMENT_ICON_COLORS[pl.icon] || PAYMENT_ICON_COLORS.link;
              const label = pl.label || PAYMENT_ICON_LABELS[pl.icon] || "Pay";
              return (
                <a
                  key={`${pl.icon}-${i}`}
                  href={pl.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`inline-flex items-center gap-1.5 text-xs font-medium border rounded-full px-3 py-1.5 hover:opacity-90 ${cls}`}
                  data-testid={`payment-link-${pl.icon || "link"}-${i}`}
                  onClick={() => {
                    try {
                      // fire-and-forget click event for analytics
                      const api = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : "/api";
                      fetch(`${api}/analytics/payment-link-click`, {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ icon: pl.icon, url: pl.url }),
                      }).catch(() => {});
                    } catch {}
                  }}
                >
                  <ExternalLink className="w-3 h-3" />
                  {label}
                </a>
              );
            })}
          </div>
          {paymentLinks.some(p => p.note) && (
            <ul className="mt-3 space-y-0.5 text-[11px] text-gray-500">
              {paymentLinks.filter(p => p.note).map((p, i) => (
                <li key={i}><strong className="text-gray-700">{p.label}:</strong> {p.note}</li>
              ))}
            </ul>
          )}
        </div>
      )}
      
      {showProducts && (
        <div className="rounded-2xl border border-gray-200 bg-white p-4" data-testid="product-upsells-card">
          <div className="flex items-center gap-2 mb-3">
            <ShoppingBag className="w-4 h-4 text-purple-600" />
            <p className="text-sm font-semibold text-gray-900">
              {currentStage === "pre" ? "What you'll need" : currentStage === "during" ? "Recommended" : "Take home"}
            </p>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {stageProducts.map((p, i) => (
              <a
                key={`${p.name}-${i}`}
                href={p.link_url || "#"}
                target={p.link_url ? "_blank" : undefined}
                rel={p.link_url ? "noopener noreferrer" : undefined}
                className="rounded-xl border border-gray-100 bg-gray-50/40 p-2 hover:bg-gray-50 transition-colors"
                data-testid={`product-upsell-${i}`}
              >
                {p.photo_url && (
                  <div className="aspect-square w-full overflow-hidden rounded-lg bg-gray-100 mb-1.5">
                    <img src={p.photo_url} alt={p.name} className="w-full h-full object-cover" />
                  </div>
                )}
                <p className="text-[11px] font-semibold text-gray-900 line-clamp-2">{p.name}</p>
                {p.price_display && <p className="text-[10px] text-gray-500">{p.price_display}</p>}
                {p.note && <p className="text-[10px] text-gray-400 mt-0.5 line-clamp-2">{p.note}</p>}
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
